import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";

import PropTypes from "prop-types";
import { Typography, useTheme } from "@mui/material";
import { Stack } from "@mui/system";

import getFileExtension from "utils/getFileExtension";
import { getIsDocumentRequired } from "utils/getIsDocumentRequired";

const imageExt = ["jpg", "png", "jpeg"];
const otherAccpetedExt = ["pdf", "doc", "docx"];

const FileInput = ({
  setFieldValue,
  documents,
  documentTypeId,
  documentCategoryId,
  documentRequiredEnum,
  utilities,
  parentId,
  isDisable = false,
  isError,
  documentFileSize = 20971520,
  imageFileSize = 20971520,
  t,
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const theme = useTheme();

  const handleFile = (e) => {
    const fileExtension = getFileExtension(
      e.target.files[0]?.name
    ).toLowerCase();
    const fileSize = e.target.files[0]?.size;
    let errorMessage = "";

    if (imageExt.includes(fileExtension) && fileSize > imageFileSize) {
      errorMessage = `File size should be less than ${imageFileSize / 1024} kb`;
    } else if (
      otherAccpetedExt.includes(fileExtension) &&
      fileSize > documentFileSize
    ) {
      errorMessage = `File size should be less than ${
        documentFileSize / Math.pow(1024, 2)
      } mb`;
    } else if (![...imageExt, ...otherAccpetedExt].includes(fileExtension)) {
      errorMessage =
        "Invalid file format, Please upload image, pdf or word file";
    }
    return errorMessage;
  };

  const handleDocument = (e) => {
    const validationErrorMessage = handleFile(e);
    setErrorMessage(validationErrorMessage);
    if (!validationErrorMessage) {
      setFieldValue("documents", [
        ...documents,
        {
          id:
            documents?.find((doc) => doc.documentTypeId === documentTypeId)
              ?.id ?? 0,
          documentTypeId: documentTypeId,
          file: e.target.files[0],
          url: URL.createObjectURL(e.target.files[0]),
          parentId: parentId ?? "",
          documentCategoryId: documentCategoryId ?? "",
          imageFileSize,
          documentFileSize,
          documentRequiredEnum,
          utilities,
        },
      ]);
    }
  };

  useEffect(() => {
    if (isError && getIsDocumentRequired(documentRequiredEnum)) {
      const isUploaded = documents.some(
        ({ documentTypeId: docTypeId }) => docTypeId === documentTypeId
      );
      if (!isUploaded) {
        setErrorMessage("Please upload the document");
      }
    }
  }, [isError]);

  return (
    <Stack>
      <input
        type="file"
        size="small"
        className="w-100"
        onChange={handleDocument}
        disabled={isDisable}
        accept="image/*, .pdf, .doc, .docx, .PDF, .DOC, .DOCX"
      />
      {!!errorMessage && (
        <Typography
          variant="caption"
          lineHeight="1.5"
          sx={{ color: theme.palette.error.main }}
        >
          {t(errorMessage)}
        </Typography>
      )}
    </Stack>
  );
};

FileInput.propTypes = {
  setFieldValue: PropTypes.func,
  documents: PropTypes.array,
  documentTypeId: PropTypes.number,
  parentId: PropTypes.number,
  isDisable: PropTypes.bool,
  documentCategoryId: PropTypes.number,
  documentRequiredEnum: PropTypes.number,
  utilities: PropTypes.array,
  isError: PropTypes.bool,
  documentFileSize: PropTypes.number,
  imageFileSize: PropTypes.number,
  t: PropTypes.func,
};

export default withTranslation()(FileInput);
